import { useState, useCallback } from 'react';
import { applyProductField } from '@/components/v2/tools/text/textGenerationApi';

export interface ApplyArgs {
  productId: string;
  fieldType: string;
  content: string;
  channelId?: string;
}

/** Write-back of a generated text into a product field, with undo (Phase 3). */
export function useAIFieldApply() {
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);

  const run = useCallback(async (productId: string, fieldType: string, content: string, channelId?: string) => {
    setApplying(true);
    setError(null);
    try {
      const resp = await applyProductField({ product_id: productId, field_type: fieldType, content, channel_id: channelId });
      setPrevious(resp.previous_value);
      setApplied(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'apply_failed');
    } finally {
      setApplying(false);
    }
  }, []);

  const apply = useCallback(
    (a: ApplyArgs) => run(a.productId, a.fieldType, a.content, a.channelId),
    [run],
  );

  const undo = useCallback(
    (a: Omit<ApplyArgs, 'content'>) => run(a.productId, a.fieldType, previous ?? '', a.channelId),
    [run, previous],
  );

  const reset = useCallback(() => {
    setApplied(false);
    setError(null);
    setPrevious(null);
  }, []);

  return { apply, undo, reset, applying, applied, error };
}
